/**
 * Server events over SSE (`/sse/{session_id}`). The browser's own retry is not used: on an error the
 * stream is closed and reopened with a growing pause (1 s … 15 s), forever — a totem must heal by
 * itself. The kiosk refetches the session snapshot when the feed is back (events may have been lost).
 */
import { reportOnce } from '../core/diagnostics';
import type { EventFeed, FeedHandlers } from '../core/ports';
import { parseServerEvent, SSE_EVENT_NAMES } from '../core/wire';

export const RETRY_DELAYS_MS = [1000, 2000, 4000, 8000, 15000] as const;

export function retryDelay(attempt: number): number {
  return RETRY_DELAYS_MS[Math.min(attempt, RETRY_DELAYS_MS.length - 1)] ?? 15000;
}

export function createSseFeed(base = '', EventSourceImpl: typeof EventSource = EventSource): EventFeed {
  return {
    open(sessionId: string, handlers: FeedHandlers) {
      let source: EventSource | null = null;
      let timer: ReturnType<typeof setTimeout> | null = null;
      let attempt = 0;
      let closed = false;

      const connect = () => {
        handlers.onStatus(attempt === 0 ? 'connecting' : 'retrying');
        source = new EventSourceImpl(`${base}/sse/${encodeURIComponent(sessionId)}`);
        source.onopen = () => {
          attempt = 0;
          handlers.onStatus('online');
        };
        source.onerror = () => {
          source?.close();
          source = null;
          if (closed) return;
          const delay = retryDelay(attempt++);
          handlers.onStatus('retrying', `reconnecting in ${delay / 1000}s`);
          timer = setTimeout(connect, delay);
        };
        for (const name of SSE_EVENT_NAMES) {
          source.addEventListener(name, (ev) => {
            let data: unknown;
            try {
              data = JSON.parse((ev as MessageEvent<string>).data);
            } catch {
              reportOnce('warn', 'transport.sse', `${name}: data is not JSON`);
              return;
            }
            const parsed = parseServerEvent(name, data);
            if (parsed.ok) handlers.onEvent(parsed.event);
            else reportOnce('warn', 'transport.sse', `${name} dropped: ${parsed.reason}`, data);
          });
        }
      };

      connect();
      return () => {
        closed = true;
        if (timer) clearTimeout(timer);
        source?.close();
      };
    },
  };
}
